import { useEffect, useState } from "react";
import { FieldGroup, FormModal, Input, SelectField, Step, Textarea } from "../../components/ui";
import { DOC_TYPES, DocTypeBadge } from "../../shared/docTypes";
import { useDocTypesEnabled } from "../../shared/DocTypesEnabledContext";
import { DEFAULT_FIRST_ORGANIZATION_CATEGORY_LABEL } from "../../shared/organizationCategories";
import { COLORS, RADIUS } from "../../shared/theme";

const emptyProof = {
  intitule: "", categorie: DEFAULT_FIRST_ORGANIZATION_CATEGORY_LABEL, docType: "ROPA", description: "",
  dateCreation: "", derniereMaj: "", responsableId: "",
  registre: "", politique: "", traitements: "", statutVerif: "À vérifier",
  dateVerif: "", verifPar: "", commentaires: "",
  statut: "En cours", version: "v1.0", dateValidation: "", validePar: "", observations: "",
};

const STATUTS_VERIF = ["Conforme", "Écart", "À vérifier"];
const STATUTS = ["En cours", "Validé", "Non conforme", "Archivé"];

export default function ProofForm({ initial, onSave, onCancel, personnes, categories, activites = [] }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial ? { ...initial } : { ...emptyProof });
  const { activeDocTypes } = useDocTypesEnabled();
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    setForm(initial ? { ...initial } : { ...emptyProof });
    setStep(1);
  }, [initial]);

  useEffect(() => {
    setForm(f => {
      if (activeDocTypes.some(d => d.code === f.docType)) return f;
      const fb = activeDocTypes[0]?.code ?? "ROPA";
      return { ...f, docType: fb };
    });
  }, [activeDocTypes]);

  useEffect(() => {
    setForm(f => {
      const labels = (categories || []).map(c => c.label).filter(Boolean);
      if (labels.length === 0) return f;
      if (labels.includes(f.categorie)) return f;
      return { ...f, categorie: labels[0] };
    });
  }, [categories]);

  const personneOptions = [
    { value: "", label: "— Sélectionner —" },
    ...personnes.map(p => ({ value: p.id, label: `${p.prenom} ${p.nom} (${p.habilitation})` })),
  ];
  const catOptions = categories.map(c => c.label);
  const activityOptions = [
    { value: "", label: "— Aucune activité liée —" },
    ...activites.map(a => ({ value: a.intitule, label: a.intitule })),
  ];
  const docChoices = activeDocTypes.length ? activeDocTypes : DOC_TYPES;
  const docTypeOptions = docChoices.map(d => ({ value: d.code, label: d.label }));
  const title = initial?.id ? "Modifier la preuve" : "Nouvelle preuve";

  return (
    <FormModal
      title={title}
      headerBackground={COLORS.primary}
      maxWidth={820}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          {step > 1 && (
            <button type="button" onClick={() => setStep(s => s - 1)} style={{ padding: "8px 18px", borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.primaryAccent}`, background: "#eff6ff", color: COLORS.primaryAccent, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>← Précédent</button>
          )}
          <button type="button" onClick={onCancel} style={{ padding: "8px 18px", borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textSecondary, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Annuler</button>
          {step < 3 ? (
            <button type="button" onClick={() => setStep(s => s + 1)} style={{ padding: "8px 18px", borderRadius: RADIUS.sm, border: "none", background: COLORS.primaryAccent, color: COLORS.white, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Suivant →</button>
          ) : (
            <button type="button" onClick={() => onSave(form)} style={{ padding: "8px 18px", borderRadius: RADIUS.sm, border: "none", background: COLORS.primary, color: COLORS.white, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>✓ Enregistrer la preuve</button>
          )}
        </div>
      }
    >
      <div style={{ display: "flex", gap: 20, marginBottom: 22, paddingBottom: 16, borderBottom: `1px solid ${COLORS.border}`, flexWrap: "wrap" }}>
        <Step number={1} title="Structuration" active={step === 1} done={step > 1} />
        <div style={{ width: 24, height: 1, background: COLORS.border, marginTop: 14 }} />
        <Step number={2} title="Liaison & cohérence" active={step === 2} done={step > 2} />
        <div style={{ width: 24, height: 1, background: COLORS.border, marginTop: 14 }} />
        <Step number={3} title="Validation & suivi" active={step === 3} done={false} />
      </div>

      {step === 1 && (
        <div>
          <div style={{ fontWeight: 700, color: COLORS.primary, marginBottom: 14, fontSize: 13 }}>Étape 1 — Structuration principale</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Intitulé"><Input value={form.intitule} onChange={set("intitule")} placeholder="Ex: Politique de protection des données" /></FieldGroup>
            </div>
            <FieldGroup label="Catégorie"><SelectField value={form.categorie} onChange={set("categorie")} options={catOptions} /></FieldGroup>
            <FieldGroup label="Type de document">
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ flex: 1 }}><SelectField value={form.docType} onChange={set("docType")} options={docTypeOptions} /></div>
                {form.docType && <DocTypeBadge code={form.docType} />}
              </div>
            </FieldGroup>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Responsable / Validateur">
                <SelectField value={form.responsableId} onChange={set("responsableId")} options={personneOptions} />
              </FieldGroup>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Description"><Textarea value={form.description} onChange={set("description")} placeholder="Description de la preuve et de son périmètre…" /></FieldGroup>
            </div>
            <FieldGroup label="Date de création"><Input type="date" value={form.dateCreation} onChange={set("dateCreation")} /></FieldGroup>
            <FieldGroup label="Dernière mise à jour"><Input type="date" value={form.derniereMaj} onChange={set("derniereMaj")} /></FieldGroup>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div style={{ fontWeight: 700, color: COLORS.primary, marginBottom: 14, fontSize: 13 }}>Étape 2 — Liaison & cohérence documentaire</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FieldGroup label="Registre des traitements"><Input value={form.registre} onChange={set("registre")} placeholder="Ex: Registre principal v3" /></FieldGroup>
            <FieldGroup label="Politique / Cartographie IT"><Input value={form.politique} onChange={set("politique")} placeholder="Ex: PSI 2024" /></FieldGroup>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Activité RGPD associée">
                <SelectField value={form.traitements} onChange={set("traitements")} options={activityOptions} />
              </FieldGroup>
            </div>
            <FieldGroup label="Statut de vérification"><SelectField value={form.statutVerif} onChange={set("statutVerif")} options={STATUTS_VERIF} /></FieldGroup>
            <FieldGroup label="Date de vérification"><Input type="date" value={form.dateVerif} onChange={set("dateVerif")} /></FieldGroup>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Vérifié par"><Input value={form.verifPar} onChange={set("verifPar")} placeholder="Ex: Audit interne" /></FieldGroup>
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Commentaires / Écarts"><Textarea value={form.commentaires} onChange={set("commentaires")} placeholder="Observations, écarts constatés…" /></FieldGroup>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <div style={{ fontWeight: 700, color: COLORS.primary, marginBottom: 14, fontSize: 13 }}>Étape 3 — Validation & suivi</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FieldGroup label="Statut"><SelectField value={form.statut} onChange={set("statut")} options={STATUTS} /></FieldGroup>
            <FieldGroup label="Version"><Input value={form.version} onChange={set("version")} placeholder="Ex: v1.0" /></FieldGroup>
            <FieldGroup label="Date de validation"><Input type="date" value={form.dateValidation} onChange={set("dateValidation")} /></FieldGroup>
            <FieldGroup label="Validé par"><Input value={form.validePar} onChange={set("validePar")} placeholder="Ex: DG – Isabelle Renard" /></FieldGroup>
            <div style={{ gridColumn: "span 2" }}>
              <FieldGroup label="Observations"><Textarea value={form.observations} onChange={set("observations")} placeholder="Notes finales, prochaines révisions…" /></FieldGroup>
            </div>
          </div>
        </div>
      )}
    </FormModal>
  );
}
